import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { ErpType } from "src/network/User";
import styles from "./RealestateField.module.css";
import { LOADING_STATE } from "src/stores/krediflow/ImportInvoiceFilesStore";
import { IRealestate } from "src/stores/krediflow/InvoiceTypes";

interface IRealestateFieldProps {
    inputWorkflowFileId: string;
}

const RealestateFieldBase = (props: IRealestateFieldProps) => {
    const { invoiceStore, authStore, importInvoiceFilesStore } = useContext(RootStoreContext);

    const { inputWorkflowFileId } = props;

    const [displayRealestateList, setDisplayRealestateList] = useState<boolean>(false);
    const [lastScrollPositionYRealestateVisaUserItems, setLastSrollYPositionRealestateVisaUserItems] =
        useState<number>(0);

    const realestateInputRef = useRef<HTMLInputElement>(null);

    const resetSearchQuery = useCallback(() => {
        const currentSelectedRealestateString = importInvoiceFilesStore.getSelectedRealestateQuery(inputWorkflowFileId);

        importInvoiceFilesStore.setCurrentRealestateSearchQuery(
            inputWorkflowFileId,
            currentSelectedRealestateString ?? ""
        );
    }, [inputWorkflowFileId, importInvoiceFilesStore]);

    useEffect(() => {
        const handleClickOutsideRealestateInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsideRealestateInputOrList = () => {
                if (!displayRealestateList || !realestateInputRef.current) {
                    return true;
                }

                const clickedInsideInput = realestateInputRef.current.contains(target);

                const clickedDropdownList =
                    target.classList.contains("dropdown-list") &&
                    target.classList.contains("inputworkflowfile-realestate");

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("inputworkflowfile-realestate");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") &&
                    target.classList.contains("inputworkflowfile-realestate");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("inputworkflowfile-realestate");

                return !(
                    clickedDropdownList ||
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsideRealestateInputOrList() && displayRealestateList) {
                setDisplayRealestateList(false);

                if (
                    importInvoiceFilesStore.isRealestateSelected(inputWorkflowFileId) &&
                    importInvoiceFilesStore.getCurrentRealestateSearchQuery(inputWorkflowFileId) === ""
                ) {
                    importInvoiceFilesStore.resetRealestate(inputWorkflowFileId);
                } else {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsideRealestateInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideRealestateInput);
        };
    }, [displayRealestateList, resetSearchQuery, inputWorkflowFileId, importInvoiceFilesStore]);

    const updateDataWithSelectedRealestate = useCallback(
        async (realestate: IRealestate, inputWorkflowFileId: string) => {
            importInvoiceFilesStore.updateDataWithSelectedRealestate(realestate, inputWorkflowFileId);
        },
        [importInvoiceFilesStore]
    );

    const onSelectRealestateItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedRealestate =
                importInvoiceFilesStore.filteredRealestateData(inputWorkflowFileId)[activeListItemIndex];

            updateDataWithSelectedRealestate(selectedRealestate, inputWorkflowFileId);
        },
        [updateDataWithSelectedRealestate, importInvoiceFilesStore, inputWorkflowFileId]
    );

    const onTabFromRealestateField = useCallback(() => {
        const realestateList = importInvoiceFilesStore.filteredRealestateData(inputWorkflowFileId);
        if (realestateList.length && realestateList.length === 1) {
            const onlyRealestateInList = realestateList[0];
            updateDataWithSelectedRealestate(onlyRealestateInList, inputWorkflowFileId);
        }
    }, [updateDataWithSelectedRealestate, inputWorkflowFileId, importInvoiceFilesStore]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "inputworkflowfile-realestate",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayRealestateList,
        isListDisplayed: displayRealestateList,
        onSelectItem: onSelectRealestateItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromRealestateField
    });

    const resetAmountOfItemsDisplayedInRealestateList = useCallback(() => {
        setLastSrollYPositionRealestateVisaUserItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangeRealestateVisaUserInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInRealestateList();

            const inputRealestateSearchQuery = e.target.value;
            importInvoiceFilesStore.setCurrentRealestateSearchQuery(inputWorkflowFileId, inputRealestateSearchQuery);
        },
        [resetAmountOfItemsDisplayedInRealestateList, inputWorkflowFileId, importInvoiceFilesStore]
    );

    const onFocusRealestateVisaUserInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (importInvoiceFilesStore.isRealestateSelected(inputWorkflowFileId) && realestateInputRef.current) {
                realestateInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInRealestateList();
            setDisplayRealestateList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInRealestateList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            inputWorkflowFileId,
            importInvoiceFilesStore
        ]
    );

    const onClickRealestateItem = useCallback(
        (realestate: IRealestate, inputWorkflowFileId: string) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayRealestateList(false);

            updateDataWithSelectedRealestate(realestate, inputWorkflowFileId);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateDataWithSelectedRealestate]
    );

    const onScrollRealestateVisaUserList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYRealestateVisaUserItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                importInvoiceFilesStore.filteredRealestateData(inputWorkflowFileId).length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionRealestateVisaUserItems(pixelsFromTop);
        },
        [lastScrollPositionYRealestateVisaUserItems, invoiceStore, importInvoiceFilesStore, inputWorkflowFileId]
    );

    const realestateVisaUserSelectionList = (
        <ul
            id="inputworkflowfile-realestate-dropdown-list"
            className={`dropdown-list inputworkflowfile-realestate krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollRealestateVisaUserList}
        >
            {importInvoiceFilesStore.loadingState === LOADING_STATE.LOADING ? (
                <li className="d-flex align-items-center justify-content-center list-item inputworkflowfile-realestate loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                importInvoiceFilesStore
                    .filteredRealestateData(inputWorkflowFileId)
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((realestate, index) => {
                        const realestateName = `${realestate.name} [${realestate.number}]`;

                        const currentRealstateSearchQuery =
                            importInvoiceFilesStore.getCurrentRealestateSearchQuery(inputWorkflowFileId);

                        const formattedRealestateName = boldenMatchedSubstring(
                            currentRealstateSearchQuery ?? "",
                            realestateName,
                            "highlighted-text inputworkflowfile-realestate"
                        );

                        return (
                            <li
                                key={index}
                                className="d-flex align-items-center list-item inputworkflowfile-realestate"
                                onClick={onClickRealestateItem(realestate, inputWorkflowFileId)}
                                id={`inputworkflowfile-realestate-list-item-${index}`}
                            >
                                <div
                                    className={`.inputworkflowfile-realestate-text ${styles.AccountingRealestateText}`}
                                    dangerouslySetInnerHTML={{
                                        __html: formattedRealestateName
                                    }}
                                />
                            </li>
                        );
                    })
            )}
        </ul>
    );

    return (
        <div className="position-relative">
            <UI.Input
                ref={realestateInputRef}
                className={styles.RealestateUserInput}
                autoComplete="off"
                type="text"
                value={importInvoiceFilesStore.getCurrentRealestateSearchQuery(inputWorkflowFileId) ?? ""}
                onChange={onChangeRealestateVisaUserInput}
                onFocus={onFocusRealestateVisaUserInput}
                onKeyDown={onKeyDownFunction}
            />
            {displayRealestateList ? realestateVisaUserSelectionList : undefined}
        </div>
    );
};

export const RealestateField = Sentry.withProfiler(observer(RealestateFieldBase));
